import { StackSidePanelContext, StackSidePanelContextProvider } from "$root/components/common/Stack/StackSidePanel";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { PageHeader } from "$root/components/PageHeader";
import { platformLayoutOptions$ } from "$root/components/PlatformRouter/layout-options";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { workspaceAdapter, type Workspace } from "$root/functional-areas/workspace/ctx";
import {
	repairWorkspaceState,
	fromWorkspace,
	WorkspaceStoreProvider,
	useWorkspaceDispatch,
	useWorkspaceSlices,
	type WorkspaceState,
} from "$root/functional-areas/workspace/state";
import { useWorkspaceSearchParams, useWorkspaceViewTabParam } from "$root/functional-areas/workspace/hooks";
import { workspaceRepository, type WorkspaceId } from "$root/functional-areas/workspace/repository";
import { viewModeFromTabParam } from "$root/functional-areas/workspace/view-tab-param";
import WorkspaceChart from "$root/functional-areas/workspace/workspaceChart";
import { WorkspaceFilterSettingsStack } from "$root/functional-areas/workspace/WorkspaceFilterSettings";
import { WorkspaceSettingsStack } from "$root/functional-areas/workspace/WorkspaceSettings";
import WorkspaceTable from "$root/functional-areas/workspace/workspaceTable";
import WorkspaceToolBar from "$root/functional-areas/workspace/WorkspaceToolbar";
import { useFunctionalAreas } from "$root/App/context";
import { useMappedQueries, useStaticQueryResultFrom } from "$root/third-party-integrations/react-query/hooks";
import { qualifier } from "$root/utils";
import { type SmartWorkspaceDTO, ViewModeEnum } from "@eu.mdotm.api/smart-workspaces";
import { Column, FlexBox, NestedTransition, Transition, type TransitionStatus } from "@mdotm/shadcn/legacy/components";
import { overrideClassList, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, useAtom, useAtomValue, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { Result } from "@mdotm/utils/runtime";
import type { QueryObserverResult } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { useEventBus } from "$root/event-bus";
import { PortfolioStudioTitleActions } from "../PortfoliosStudio/PortfolioStudioTitleActions";

/**
 * Prefix of the MCP tools scoped to workspaces. sphere-mcp enforces an `<entity>-<description>`
 * naming rule (there's a test on it), so this derives the scope from the name instead of maintaining
 * a list of the individual workspace write tools. Declared here, in the consumer, because what it
 * encodes is "which of the bot's writes can affect THIS screen".
 */
const WORKSPACE_TOOL_PREFIX = "workspace-";

function WorkspaceInContext(props: { tab: string | undefined }) {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const slices = useWorkspaceSlices();
	const dispatch = useWorkspaceDispatch();
	const nOfFilters = useAtomValue(slices.filters$).length;

	const pagination = useAtomValue(slices.pagination$);
	const [, setPlatformLayoutOptions] = useAtom(platformLayoutOptions$);

	const { preselectedInvestments } = useWorkspaceSearchParams();
	useWorkspaceViewTabParam(props.tab);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <sync inital view>
	useEffect(() => {
		const view = readAtom(slices.view$);
		setPlatformLayoutOptions((latest) => ({
			...latest,
			maxPageHeight: view === ViewModeEnum.Table ? "screen" : "auto",
		}));
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <apply uuid from preselected only>
	useEffect(() => {
		if (preselectedInvestments && preselectedInvestments.length > 0) {
			void dispatch({
				type: "upsertFilter",
				chain: {
					name: "Filter selection",
					comparisonMode: "in",
					comparisonValue: [preselectedInvestments],
					concept: {
						id: "systemId: name",
						dataType: "SPHERE_ENTITY",
					},
					source: "CURRENT",
					timeHorizon: undefined,
				},
			});
		}
	}, [preselectedInvestments]);

	return (
		<Column minSize="0" gap={16}>
			<WithAtom atom$={slices.view$}>
				{(view) => (
					<WithAtom atom$={slices.search$}>
						{(search) => (
							<WorkspaceToolBar<ViewModeEnum>
								filtered={
									view === "CHART"
										? pagination.total
										: Math.min(pagination.size, pagination.total - (pagination.page - 1) * pagination.size)
								}
								total={pagination.total}
								label="Portfolios"
								tabValue={view}
								onChangeTab={(view) => {
									void dispatch({ type: "setView", view });
								}}
								tabOptions={[
									{
										icon: "menu",
										children: "Table",
										value: ViewModeEnum.Table,
									},
									{
										icon: "toolchart-cart",
										children: "Chart",
										value: ViewModeEnum.Chart,
									},
								]}
								actions={[
									{
										children: "Filter",
										icon: { icon: "Filter-no" },
										dot: nOfFilters ? { counter: nOfFilters } : undefined,
										onClick() {
											sidePanelCtx.push(WorkspaceFilterSettingsStack);
										},
										"data-qualifier": qualifier.component.button("WorkspaceFilters"),
									},
									{
										children: "Display settings",
										icon: { icon: "Settings" },
										onClick() {
											sidePanelCtx.setOpen(true);
										},
										"data-qualifier": qualifier.component.button("WorkspaceSettings"),
									},
								]}
								search={search}
								onChangeSearch={(search) => {
									void dispatch({ type: "setSearch", search });
								}}
							/>
						)}
					</WithAtom>
				)}
			</WithAtom>
			<WorkspaceContent />
		</Column>
	);
}
const visualizations = [
	{ Component: WorkspaceTable, type: ViewModeEnum.Table },
	{ Component: WorkspaceChart, type: ViewModeEnum.Chart },
] satisfies Array<{ Component: React.ComponentType; type: ViewModeEnum }>;

function WorkspaceContent() {
	const slices = useWorkspaceSlices();

	return (
		<div className="min-h-0 h-full grid grid-cols-1">
			<WithAtom atom$={slices.view$}>
				{(view) => (
					<ForEach collection={visualizations} keyProvider={(v) => v.type}>
						{(Workspace) => (
							<div
								className={toClassName({
									"col-start-1 row-start-1 flex flex-col": true,
									"min-h-[calc(100dvh-183px)]": Workspace.type === ViewModeEnum.Chart,
								})}
							>
								<WorkspaceTransition in={Workspace.type === view}>
									{/* <Activity mode={Workspace.type === view ? "visible" : "hidden"}> */}
									<Workspace.Component />
									{/* </Activity> */}
								</WorkspaceTransition>
							</div>
						)}
					</ForEach>
				)}
			</WithAtom>
		</div>
	);
}

function WorkspaceTransition(props: {
	children: ReactNode;
	in: boolean;
	onTransitionChange?(status: TransitionStatus): void;
}) {
	return (
		<Transition in={props.in} onChange={(status) => props.onTransitionChange?.(status)}>
			<NestedTransition
				className={overrideClassList(
					"max-w-full h-full transition-all transition-discrete ease-in-out duration-400 flex flex-col bg-white",
				)}
				enterFromClassName={"opacity-0"}
				enterToClassName={"opacity-100 z-10"}
				exitedClassName="invisible z-0 overflow-y-hidden"
			>
				{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
					<div
						data-transitionstatus={transitionStatus}
						className={toClassName(animatedClassList)}
						style={animatedStyle}
					>
						{props.children}
					</div>
				)}
			</NestedTransition>
		</Transition>
	);
}

function WorkspacePage(props: { id: WorkspaceId; tab?: string }) {
	return <WorkspacePageWithIdInner id={props.id} tab={props.tab} />;
}

export function WorkspacePageDefaultInner(props: { tab?: string }) {
	const workspaceCtxQuery = useStaticQueryResultFrom(Result.ok(workspaceAdapter.toContext()));
	const { data: defaultWorkspace } = workspaceRepository.getDefaultWorkspace.useQuery();
	return defaultWorkspace?.uuid ? (
		<WorkspacePageWithIdInner id={defaultWorkspace?.uuid} tab={props.tab} />
	) : (
		<WorkspacePageInner workspaceCtxQuery={workspaceCtxQuery} tab={props.tab} />
	);
}

function WorkspacePageWithIdInner(props: { id: WorkspaceId; tab?: string }) {
	const workspaceQuery = workspaceRepository.getWorkspace.useQuery(props.id);
	const workspaceSharedQuery = workspaceRepository.getSharedConcepts.useQuery();
	const workspaceCtxQuery = useMappedQueries(
		([workspace, sharedParametrizedConcepts]) =>
			workspaceAdapter.toContext({ workspace, sharedParametrizedConcepts: sharedParametrizedConcepts.sharedConcepts }),
		[],
		[workspaceQuery, workspaceSharedQuery],
	);

	// RE-INIT AFTER A BOT WRITE (MDOT-24442). The queries above refetch on their own — the bot's
	// write invalidates them like any other — but the refreshed data would go nowhere:
	// `WorkspaceStoreProvider` is seeded in a `useState` initializer, i.e. ONCE per mount, and every
	// screen below reads the store rather than the query. That seed-once is deliberate, not an
	// oversight: local edits autosave and `updateWorkspace` invalidates `getWorkspace`, so fresh props
	// arrive after every column drag, and reacting to them would fight the user's own editing. The
	// event is what distinguishes "the server changed under me" from that local echo.
	//
	// So: a remount, which is the cheapest mechanism that is correct. What it discards is the `ui` half
	// of the store — search, selection, page — plus the open side panel and the scroll position;
	// nothing server-side is recomputed and ingestion is pure. The cost is therefore a repaint at the
	// moment the user has just asked the bot to change this very workspace, and keying a subtree to
	// reset it is the documented way to do that.
	//
	// The alternative — merging fresh props into the seeded store — has to decide which local edit wins
	// for every field. That is real machinery (`portfolio-lab/hooks/useBotWriteResync.ts` is what it
	// looks like on a screen that genuinely needs it) and its failure mode is losing an edit rather
	// than a search term. Not the trade to make here, where an external write is the rare case.
	//
	// Nor is holding a working copy between saves a departure from "react-query owns server state":
	// this screen is an editor, and that working copy is what makes its autosave coherent.
	const [reinitEpoch, setReinitEpoch] = useState(0);
	useEventBus("bot-write-settled", {
		// The bot named this workspace, OR it called a `workspace-*` write whose target we can't pin
		// down. The second half is a deliberate fail-open: `workspace-rename` / `workspace-move` carry
		// the uuid, but the shared-concept writes name the CONCEPT (while `getSharedConcepts` feeds a
		// seeded atom, so they do change this screen), and the folder tools name a path. Guessing
		// "unaffected" there is the stale screen this whole mechanism exists to prevent, so an
		// unattributable workspace write re-inits. The cost is a spurious flash on, say, the bot
		// creating an unrelated folder — cheap, and rare.
		filter: ({ toolNames, uuids }) =>
			uuids.includes(props.id.toLowerCase()) ||
			(!uuids.length && toolNames.some((name) => name.startsWith(WORKSPACE_TOOL_PREFIX))),
		listener: () => setReinitEpoch((epoch) => epoch + 1),
	});

	return <WorkspacePageInner workspaceCtxQuery={workspaceCtxQuery} tab={props.tab} reinitEpoch={reinitEpoch} />;
}

function WorkspacePageInner(props: {
	workspaceCtxQuery: QueryObserverResult<Workspace, unknown>;
	tab: string | undefined;
	/** Bumped to force a re-seed of the workspace context — see {@link WorkspacePageWithIdInner}. */
	reinitEpoch?: number;
}) {
	// A `tab` in the URL outranks the workspace's persisted `viewMode`, and seeding the context with
	// it (rather than switching after mount) means a deep link paints the requested tab straight
	// away instead of flashing the saved one. From there on the two are kept in sync by
	// `useWorkspaceViewTabParam`.
	const requestedView = viewModeFromTabParam(props.tab);

	return (
		<div className="min-h-0 flex flex-col grow bg-(--palette-N50) relative">
			<ReactQueryWrapperBase query={props.workspaceCtxQuery}>
				{(initWorkspaceCtx) => (
					<WorkspaceRoot
						workspace={requestedView ? { ...initWorkspaceCtx, view: requestedView } : initWorkspaceCtx}
						tab={props.tab}
						key={`${initWorkspaceCtx.workspace?.uuid}:${props.reinitEpoch ?? 0}`}
					/>
				)}
			</ReactQueryWrapperBase>
		</div>
	);
}

/**
 * Ingestion, once per document.
 *
 * The deserialized workspace is **repaired** before it becomes state: from here on a broken reference is a
 * programming error that throws, which is only defensible because this ran first (see `state/ingest.ts`).
 */
function WorkspaceRoot(props: { workspace: Workspace; tab: string | undefined }) {
	const initialState = useState<WorkspaceState>(() => repairWorkspaceState(fromWorkspace(props.workspace)).state)[0];

	return (
		<WorkspaceStoreProvider initialState={initialState}>
			<StackSidePanelContextProvider root={WorkspaceSettingsStack}>
				<UpstreamDocumentSync document={props.workspace.workspace} />
				<WorkspaceHeader />
				<WorkspaceInContext tab={props.tab} />
			</StackSidePanelContextProvider>
		</WorkspaceStoreProvider>
	);
}

/**
 * A rename or a move that happened somewhere else, back into the store.
 *
 * The store is seeded once per mount, on purpose — see {@link WorkspacePageWithIdInner} — and everything
 * below it reads the store rather than the query. That is right for the workspace's *content*, which this
 * screen owns and autosaves, and wrong for the two fields it does not own: `name` and `taxonomy`. Those
 * change through APIs of their own — the app drawer's Rename and Move to folder, the same actions in
 * another tab, SphereAI — each of which invalidates `getWorkspace`, so the refetch was already happening
 * and only had nowhere to land.
 *
 * `documentRefreshed` is where it lands, and it can be dispatched on every refetch because the reducer
 * writes a value the store compares: a document that came back unchanged is not a state change. It reaches
 * neither content nor the PUT, which is what makes it safe to run while the user is editing — see the note
 * on the action for why.
 *
 * Not a remount, which is the other way to adopt fresh props and what a bot write does: a remount discards
 * search, selection, page and the open side panel, and a rename is a field, not a new document.
 */
function UpstreamDocumentSync(props: { document: SmartWorkspaceDTO | undefined }): null {
	const dispatch = useWorkspaceDispatch();
	const document = props.document;

	useEffect(() => {
		if (document) {
			void dispatch({ type: "documentRefreshed", document });
		}
	}, [dispatch, document]);

	return null;
}

function WorkspaceHeader() {
	const slices = useWorkspaceSlices();
	// The upstream document, not the client's model of it: `name` is one of the fields the store keeps only
	// on `meta.document` (see `WorkspaceSlices.document$`), and it is what SphereAI needs to name the screen.
	const document = useAtomValue(slices.document$);
	const uuid = document?.uuid;
	const view = useAtomValue(slices.view$);

	// Expose the open workspace to SphereAI so it can ground answers / navigate here — `view` lets it know
	// which tab the user is looking at, not just which workspace.
	useFunctionalAreas(
		() => ({ areas: "workspace", data: { uuid, name: document?.name, view } }),
		[uuid, document?.name, view],
	);

	const crumbs = useCrumbs();

	return (
		<PageHeader
			crumbs={[...crumbs.portfolioStudio("Portfolios"), ...crumbs.useWorkspacePath(uuid)]}
			titleAction={
				<FlexBox gap={8} alignItems="center" justifyContent="space-between">
					<PortfolioStudioTitleActions />
				</FlexBox>
			}
		/>
	);
}

export default WorkspacePage;
